import React, { useState, useRef, useEffect } from "react";
import {
  MoreVertical,
  Printer,
  Power,
  PowerOff,
  Trash2,
  CheckCircle2,
  XCircle,
  Loader2,
  PrinterIcon
} from "lucide-react";
import { usePrinters } from "../hooks/usePrinters";

const PrinterDeviceCard = ({ printer }) => {
  const menuRef = useRef(null);
  const { updatePrinterConnection, deletePrinter } = usePrinters();
  
  // Separate loading states for each action
  const [loadingStates, setLoadingStates] = useState({
    connect: false,
    disconnect: false,
    delete: false
  });

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Check if any operation is loading
  const isAnyLoading = Object.values(loadingStates).some(state => state);

  // Click outside handler
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleConnect = async (isConnected) => {
    const actionType = isConnected ? 'connect' : 'disconnect';
    setLoadingStates(prev => ({ ...prev, [actionType]: true }));
    
    try {
      await updatePrinterConnection(printer, isConnected);
      setIsMenuOpen(false);
    } catch (error) {
      console.error("Failed to connect printer:", error);
    } finally {
      setLoadingStates(prev => ({ ...prev, [actionType]: false }));
    }
  };

  const handleDelete = async () => {
    setLoadingStates(prev => ({ ...prev, delete: true }));
    try {
      const success = await deletePrinter(printer.id);
      if (success) {
        setIsMenuOpen(false);
      }
    } finally {
      setLoadingStates(prev => ({ ...prev, delete: false }));
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-300 flex flex-col items-center justify-between p-0 shadow-lg hover:shadow-xl transition-shadow relative h-44">
      {/* Printer logo box */}
      <div className="w-full flex justify-center items-center bg-gray-50 rounded-t-2xl border-b border-gray-100 h-16 relative">
        <Printer className="w-8 h-8 text-blue-500" />
        <div ref={menuRef} className="absolute top-4 right-2 z-10">
          <button
            type="button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            disabled={isAnyLoading}
            className="p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <MoreVertical size={20} />
          </button>
          {isMenuOpen && (
            <div
              className="absolute right-0 mt-2 w-48 rounded-lg border border-gray-100 bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 z-20"
            >
              <div className="px-1 py-1">
                {printer.isConnected ? (
                  <button
                    onClick={() => handleConnect(false)}
                    disabled={isAnyLoading}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-gray-900 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingStates.disconnect ? (
                      <Loader2 size={16} className="animate-spin text-gray-900" />
                    ) : (
                      <PowerOff size={16} className="text-gray-900" />
                    )}
                    Disconnect
                  </button>
                ) : (
                  <button
                    onClick={() => handleConnect(true)}
                    disabled={isAnyLoading}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-gray-900 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingStates.connect ? (
                      <Loader2 size={16} className="animate-spin text-gray-900" />
                    ) : (
                      <Power size={16} className="text-gray-900" />
                    )}
                    Connect
                  </button>
                )}
                <div className="h-px my-1 bg-gray-200" />
                <button
                  onClick={handleDelete}
                  disabled={isAnyLoading}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-red-600 rounded-md hover:bg-red-50 group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingStates.delete ? (
                    <Loader2 size={16} className="animate-spin text-red-600" />
                  ) : (
                    <Trash2 size={16} className="text-red-600" />
                  )}
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Printer name and info */}
      <div className="flex-1 flex flex-col items-center justify-center w-full px-2">
        <div className="text-gray-900 font-semibold text-lg mb-1 truncate w-full text-center mt-2">
          {printer?.name}
        </div>
        <div className="text-xs text-gray-500 w-full text-center mb-1">
          {printer.type} | {printer.location}
        </div>
      </div>

      {/* Status */}
      <div className="flex items-center gap-2 mb-3">
        {printer.isConnected ? (
          <CheckCircle2 size={16} className="text-green-500" />
        ) : (
          <XCircle size={16} className="text-gray-400" />
        )}
        <span
          className={`text-sm font-medium ${
            printer.isConnected ? "text-green-600" : "text-gray-400"
          }`}
        >
          {printer.isConnected ? "Connected" : "Disconnected"}
        </span>
      </div>

      {/* Global loading overlay - removed since we have per-action loading states */}
    </div>
  );
};

export default PrinterDeviceCard;
